import React, { useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import {
  useResubscribeScreenMutation,
  useRevokeScreenMutation,
} from "../../../../services";
import ConfirmActionModal from "./ConfirmActionModal";

interface AddonScreenDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  license: any;
  addOnScreens?: any[];
  onDeleteScreen?: (screenId: string) => void;
}

const AddonScreenDetailsModal: React.FC<AddonScreenDetailsModalProps> = ({
  isOpen,
  onClose,
  license,
  addOnScreens = [],
  onDeleteScreen,
}) => {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [resubscribeScreen] = useResubscribeScreenMutation();
  const [revokeScreen] = useRevokeScreenMutation();
  const [showResubscribeConfirm, setShowResubscribeConfirm] = useState(false);
  const [selectedScreenId, setSelectedScreenId] = useState<string | null>(null);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [selectedRevokeScreenId, setSelectedRevokeScreenId] = useState<string | null>(null);
  const isLicenseCanceling = license?.isCancelRequested || license?.isCancelAccepted;
  const isExpired = license?.status?.toLowerCase() === "expired";

  if (!isOpen) return null;

  const handleOpenResubscribe = (screenId: string) => {
    setSelectedScreenId(screenId);
    setShowResubscribeConfirm(true);
  };

  const handleConfirmResubscribe = async () => {
    if (!selectedScreenId) return;
    try {
      const resp = await resubscribeScreen({
        screenId: selectedScreenId,
      }).unwrap();
      if (resp?.result?.invoice?.invoiceUrl) {
        window.open(resp.result.invoice.invoiceUrl, "_blank");
      }
      console.log("Screen resubscribed successfully!");
    } catch (e) {
      console.error("Failed to resubscribe screen:", e);
    }
    setShowResubscribeConfirm(false);
    setSelectedScreenId(null);
  };

  const handleOpenRevoke = (screenId: string) => {
    setSelectedRevokeScreenId(screenId);
    setShowRevokeConfirm(true);
  };

  const handleConfirmRevoke = async () => {
    if (!selectedRevokeScreenId) return;
    try {
      await revokeScreen({ screenId: selectedRevokeScreenId }).unwrap();
      console.log("Screen revocation successful!");
    } catch (e) {
      console.error("Failed to revoke screen:", e);
    }
    setShowRevokeConfirm(false);
    setSelectedRevokeScreenId(null);
  };

  const formatDateDMY = (value: any): string => {
    if (!value) return "--";
    const d = new Date(value);
    if (!isNaN(d.getTime())) {
      const dd = String(d.getDate()).padStart(2, "0");
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const yy = String(d.getFullYear()).slice(-2);
      return `${dd}-${mm}-${yy}`;
    }
    return String(value);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className={`w-full max-w-[480px] rounded-lg shadow-2xl border ${isDark
          ? "bg-[#1B1B1B] border-[#3A3A3A] text-gray-100"
          : "bg-white border-gray-200 text-gray-900"
          }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-5 py-3 border-b ${isDark ? "border-[#3A3A3A]" : "border-gray-200"
            }`}
        >
          <h3
            className={`text-sm font-semibold ${isDark ? "text-white" : "text-gray-900"
              }`}
          >
            Addons Details
          </h3>
          <button
            onClick={onClose}
            className={`w-6 h-6 flex items-center justify-center rounded-[7.529px] transition-opacity ${isDark
              ? "bg-[#2E2E2E] shadow-[0_-0.941px_0.941px_0_rgba(150,161,172,0.12)_inset] hover:opacity-80"
              : "bg-gray-100 hover:bg-gray-200"
              }`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M12.4235 2.63525L2.63525 12.4235M2.63525 2.63525L12.4235 12.4235"
                stroke={isDark ? "#F3F3F6" : "#4B5563"}
                strokeWidth="1.12942"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 text-[11px] space-y-5">
          {/* Top summary grid - 4 columns layout for precise alignment */}
          <div
            className={`grid grid-cols-[auto_1fr_auto_1fr] gap-x-12 gap-y-4 rounded-md px-4 py-4 text-[11px] ${isDark ? "bg-[#252525]" : "bg-gray-50"
              }`}
          >
            {/* Row 1 */}
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              License ID
            </div>
            <div className="text-[13px] font-bold text-[#EDF5C0]">
              {addOnScreens?.[0]?.code || license?.code || license?._id || "--"}
            </div>
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
             Status
            </div>
            <div className="text-[13px] font-bold text-[#C6D92C]">
              {license?.status || "--"}
            </div>
               <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Assigned to
            </div>
            <div className={`text-[13px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {license.assignedGym?.name || "--"}
            </div>
            

            {/* Row 2 */}
         <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              License Type
            </div>
            <div className="text-[13px] font-bold text-[#EDF5C0]">
              {addOnScreens?.[0]?.type || license?.type || "--"}
            </div>
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Location
            </div>
            <div className={`text-[13px]  ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {license.assignedGym?.location || "--"}
            </div>

            {/* Row 3 */}
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Billed Date
            </div>
            <div className={`text-[13px] font-bold ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {formatDateDMY(license?.startDate)}
            </div>
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              End Date
            </div>
            <div className={`text-[13px]  ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {formatDateDMY(license?.endDate)}
            </div>

            {/* Row 4 */}
            <div className={`text-[12px] font-semibold ${isDark ? "text-[#9B9DB6]" : "text-gray-500"}`}>
              Total
            </div>
            <div className={`text-[13px]  ${isDark ? "text-gray-200" : "text-gray-900"}`}>
              {license?.totalAmount ?? 0}
            </div>
            <div></div>
            <div></div>
          </div>

          {/* Addon Screens Details */}
          <div className="space-y-2">
            <div
              className={`text-[12px] font-medium ${isDark ? "text-gray-200" : "text-gray-800"
                }`}
            >
              Addon Screens Details
            </div>
            <div
              className={`rounded-md border overflow-hidden text-[11px] ${isDark
                ? "border-[#3A3A3A] bg-[#111111]"
                : "border-gray-200 bg-gray-50"
                }`}
            >
              {/* Sticky Header */}
              <div
                className={`sticky top-0 grid grid-cols-[1fr_1.5fr_120px] items-center px-4 py-2 border-b font-semibold ${isDark
                  ? "border-[#3A3A3A] text-gray-400 bg-[#1B1B1B]"
                  : "border-gray-200 text-gray-500 bg-gray-50"
                  }`}
              >
                <span>ID</span>
                <span>Status</span>
                <span className="text-right"></span>
              </div>

              {/* Scrollable Content */}
              <div
                className={`max-h-[250px] overflow-y-auto ${isDark ? "bg-[#111111]" : "bg-white"
                  }`}
              >
                {addOnScreens.length === 0 ? (
                  <div className="px-4 py-2 text-gray-400">
                    No addon screens found.
                  </div>
                ) : (
                  addOnScreens.map((screen: any, index: number) => {
                    const screenId =
                      screen.screenId || screen.code || screen._id;
                    const isCancelRequested = !!screen.isCancelRequested;
                    const isCancelAccepted = !!screen.isCancelAccepted;
                    const isDowngradeRequested = !!screen.isDowngradeRequested;
                    const isDowngradeAccepted = !!screen.isDowngradeAccepted;
                    const showRevoke = isCancelRequested || isCancelAccepted;
                    const showResubscribe = screen.status === "cancelled";

                    return (
                      <div
                        key={index}
                        className={`grid grid-cols-[1fr_1.5fr_120px] items-center px-4 py-3 ${index !== 0 ? "border-t" : ""
                          } ${isDark ? "border-[#3A3A3A]" : "border-gray-200"}`}
                      >
                        {/* ID Column */}
                        <span
                          className={`${isDark ? "text-gray-300" : "text-gray-700"
                            }`}
                        >
                          {screenId}
                        </span>

                        {/* Status Column */}
                        <div>
                          {showResubscribe ? (
                            <span
                              className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"
                                }`}
                            >
                              Expired at {formatDateDMY(screen.endDate)}
                            </span>
                          ) : showRevoke ? (
                            <span className="text-[#C6D92C] font-medium">
                              {isCancelAccepted
                                ? "Cancellation Accepted"
                                : "Cancellation in progress"}
                            </span>
                          ) : isDowngradeAccepted || isDowngradeRequested ? (
                            <span className="text-[#C6D92C] font-medium">
                              {isDowngradeAccepted
                                ? "Downgraded"
                                : "Downgrading"}
                            </span>
                          ) : (
                            <span className="text-[#C6D92C] font-medium">
                              {screen.status || "Active"}
                            </span>
                          )}
                        </div>

                        {/* Action Column */}
                        <div className="flex justify-end">
                          {showResubscribe ? (
                            <button
                              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1.5 text-white shadow-lg transition-all ${isLicenseCanceling ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.02] active:scale-[0.98]"}`}
                              style={isLicenseCanceling ? { background: "#555" } : {
                                background:
                                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 1) 0%, rgba(83, 91, 16, 1) 100%)",
                              }}
                              onClick={() => {
                                if (!isLicenseCanceling) handleOpenResubscribe(screen._id);
                              }}
                              title={isLicenseCanceling ? "License cancel in progress" : "Resubscribe to this screen"}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M16.3884 3L17.3913 3.97574C17.8393 4.41165 18.0633 4.62961 17.9844 4.81481C17.9056 5 17.5888 5 16.9552 5H9.19422C5.22096 5 2 8.13401 2 12C2 13.4872 2.47668 14.8662 3.2895 16"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M7.61156 21L6.60875 20.0243C6.16074 19.5883 5.93673 19.3704 6.01557 19.1852C6.09441 19 6.4112 19 7.04478 19H14.8058C18.779 19 22 15.866 22 12C22 10.5128 21.5233 9.13383 20.7105 8"
                                  stroke="white"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Resubscribe
                            </button>
                          ) : showRevoke ? (
                            <button
                              className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold flex items-center gap-1.5 text-white shadow-lg transition-all ${isLicenseCanceling ? "opacity-50 cursor-not-allowed" : "hover:scale-[1.02] active:scale-[0.98]"}`}
                              style={isLicenseCanceling ? { background: "#555" } : {
                                background:
                                  "radial-gradient(94.17% 100% at 52.48% 0%, rgba(198, 217, 44, 1) 0%, rgba(83, 91, 16, 1) 100%)",
                              }}
                              onClick={() => {
                                if (!isLicenseCanceling) handleOpenRevoke(screen._id);
                              }}
                              title={isLicenseCanceling ? "License cancel in progress" : "Revoke cancellation request"}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 4v6h6" />
                                <path d="M3.51 9A9 9 0 1 0 6 6.3L1 10" />
                              </svg>
                              Revoke
                            </button>
                          ) : (
                            <button
                              className={`p-1.5 transition-colors rounded-lg ${isDowngradeRequested || isLicenseCanceling || isExpired
                                ? "text-gray-500 cursor-not-allowed"
                                : "text-red-500 hover:bg-red-500/10"
                                }`}
                              title={
                                isExpired
                                  ? "The license is expired so that delete action not able done"
                                  : isLicenseCanceling
                                    ? "License is canceling"
                                    : isDowngradeRequested
                                      ? "already downgraded screen now can't be deleted"
                                      : "Cancel Screen"
                              }
                              disabled={isDowngradeRequested || isLicenseCanceling || isExpired}
                              onClick={() => {
                                if (onDeleteScreen && !isDowngradeRequested && !isLicenseCanceling && !isExpired) {
                                  onDeleteScreen(screen._id);
                                }
                              }}
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                              >
                                <path
                                  d="M18.8334 8.5V18.6667C18.8334 19.2275 18.611 19.7654 18.2151 20.1613C17.8192 20.5571 17.2813 20.7797 16.7206 20.7797H7.27941C6.71869 20.7797 6.18078 20.5571 5.78486 20.1613C5.38895 19.7654 5.16642 19.2275 5.16642 18.6667V8.5M3.05536 6.38983H20.9442M14.6109 6.38983V4.27797C14.6109 3.71725 14.3884 3.17934 13.9925 2.78342C13.5966 2.38751 13.0586 2.16498 12.4979 2.16498H11.5016C10.9409 2.16498 10.403 2.38751 10.0071 2.78342C9.61114 3.17934 9.38861 3.71725 9.38861 4.27797V6.38983M10.4447 11.6667V17.6102M13.5553 11.6667V17.6102"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ConfirmActionModal
        isOpen={showResubscribeConfirm}
        onClose={() => setShowResubscribeConfirm(false)}
        onConfirm={handleConfirmResubscribe}
        title="Resubscribe Screen"
        message="Are you sure you want to resubscribe to this screen?"
        confirmText="Confirm and Resubscribe"
        type="action"
      />

      <ConfirmActionModal
        isOpen={showRevokeConfirm}
        onClose={() => setShowRevokeConfirm(false)}
        onConfirm={handleConfirmRevoke}
        title="Revoke Screen Cancellation"
        message="Are you sure you want to revoke the cancellation request for this screen?"
        confirmText="Confirm Revoke"
        type="delete"
      />
    </div>
  );
};

export default AddonScreenDetailsModal;
